/**
 * Sharing a garden — the seed string, and nothing else.
 *
 * A garden is packed into a couple of hundred bytes and carried in the URL, so
 * a link recreates it in somebody else's room. There is no server, no upload
 * and no account: the link *is* the garden.
 *
 * ## The privacy line, and where it moves
 *
 * Everywhere else in this project, nothing typed by a player leaves the device
 * (`AGENTS.md`). This is the one exception, and it is an exception only
 * because the player performs it deliberately: they open the share sheet, they
 * choose whether the name goes in, and they choose who to send it to. The
 * name is **opt-in and off by default** for that reason. Nothing here is ever
 * sent anywhere by the app itself.
 *
 * ## Why a hand-packed binary rather than JSON
 *
 * A full tier-3 garden is 54 specimens. As JSON-in-base64 that is a couple of
 * kilobytes of URL, which is unusable in a message and impossible in a QR
 * code — and a QR code on a poster is exactly how this project expects to
 * be opened. Four bytes a plant keeps a full garden near 250 characters.
 */

import { SPECIES, WIRE_SPECIES, type SpeciesId } from '../content/species';
import { TIERS } from '../content/tiers';

const VERSION = 1;
const MAX_NAME_BYTES = 48;
/** Ages are minutes in a u16, so a little over 45 days. Older simply caps. */
const MAX_AGE_MIN = 0xffff;

export interface SharedPlant {
  speciesId: SpeciesId;
  slot: number;
  /** Minutes between sowing and the moment the link was made. */
  ageMinutes: number;
}

export interface SharedGarden {
  /** Empty when the sharer chose not to include it. */
  name: string;
  tier: number;
  plants: SharedPlant[];
}

/* ------------------------------------------------------------------ *
 * base64url
 * ------------------------------------------------------------------ */

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(text: string): Uint8Array | null {
  try {
    const padded = text.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ *
 * Encode
 * ------------------------------------------------------------------ */

export function encodeGarden(garden: SharedGarden): string {
  const nameBytes = new TextEncoder().encode(garden.name).slice(0, MAX_NAME_BYTES);
  const plants = garden.plants.filter(
    (p) => WIRE_SPECIES.includes(p.speciesId) && p.slot >= 0 && p.slot < 255,
  );

  const bytes = new Uint8Array(3 + plants.length * 4 + 1 + nameBytes.length);
  const view = new DataView(bytes.buffer);
  bytes[0] = VERSION;
  bytes[1] = Math.max(0, Math.min(TIERS.length - 1, garden.tier));
  bytes[2] = Math.min(255, plants.length);

  let at = 3;
  for (const plant of plants.slice(0, 255)) {
    bytes[at] = plant.slot;
    bytes[at + 1] = WIRE_SPECIES.indexOf(plant.speciesId);
    view.setUint16(at + 2, Math.max(0, Math.min(MAX_AGE_MIN, Math.round(plant.ageMinutes))), true);
    at += 4;
  }

  bytes[at] = nameBytes.length;
  bytes.set(nameBytes, at + 1);
  return toBase64Url(bytes);
}

/* ------------------------------------------------------------------ *
 * Decode
 * ------------------------------------------------------------------ */

/**
 * Read a seed string back.
 *
 * Deliberately paranoid: this is the only input to the game that arrives from
 * outside the device, and it arrives as a URL anyone can retype. Anything
 * malformed returns null rather than throwing, and a plant that does not make
 * sense is skipped rather than taking the whole garden down with it.
 */
export function decodeGarden(seed: string): SharedGarden | null {
  const bytes = fromBase64Url(seed.trim());
  if (!bytes || bytes.length < 4) return null;
  if (bytes[0] !== VERSION) return null;

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const tier = Math.max(0, Math.min(TIERS.length - 1, bytes[1]));
  const count = bytes[2];
  if (bytes.length < 3 + count * 4 + 1) return null;

  const plants: SharedPlant[] = [];
  const taken = new Set<number>();
  let at = 3;
  for (let i = 0; i < count; i++, at += 4) {
    const slot = bytes[at];
    const speciesId = WIRE_SPECIES[bytes[at + 1]];
    // Skip rather than fail: an unknown species index means the link was made
    // by a newer build, and the rest of the garden is still worth showing.
    if (!speciesId || !SPECIES[speciesId]) continue;
    if (taken.has(slot)) continue;
    taken.add(slot);
    plants.push({ speciesId, slot, ageMinutes: view.getUint16(at + 2, true) });
  }

  const nameLen = bytes[at];
  if (nameLen === undefined || bytes.length < at + 1 + nameLen) return null;
  let name = '';
  try {
    name = new TextDecoder().decode(bytes.slice(at + 1, at + 1 + nameLen));
  } catch {
    name = '';
  }

  // Strip control characters, by code point rather than by a regex range.
  // The name may legitimately be Arabic, so a printable-ASCII range is wrong.
  const clean = [...name]
    .filter((ch) => {
      const code = ch.codePointAt(0) ?? 0;
      return code >= 0x20 && code !== 0x7f;
    })
    .join('')
    .slice(0, 24);
  return { name: clean, tier, plants };
}

/** The full link for a seed, against whatever origin the app is served from. */
export function shareUrl(seed: string): string {
  const url = new URL(window.location.href);
  url.hash = '';
  url.search = `?g=${seed}`;
  return url.toString();
}

/** The seed in the current URL, if there is one. */
export function seedFromLocation(): string | null {
  const seed = new URLSearchParams(window.location.search).get('g');
  return seed && seed.length > 4 ? seed : null;
}
